import React, { useEffect, useState } from "react";
import { collection, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AOS from "aos";
import "aos/dist/aos.css";

const Dashboard = () => {
  const [signups, setSignups] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 600, once: true });

    const unsubscribe = onSnapshot(collection(db, "signups"), (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSignups(data);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    signOut(auth).then(() => navigate("/login"));
  };

  const markAsPaid = async (id) => {
    try {
      const ref = doc(db, "signups", id);
      await updateDoc(ref, { paymentStatus: "Paid" });
      setSuccessMessage("Payment marked as Paid!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      alert("Failed to update payment status.");
      console.error(err);
    }
  };

  const exportCSV = () => {
    if (signups.length === 0) return;
    const csvData = signups.map(
      ({ fullName, email, phone, paymentStatus, timestamp }) => ({
        "Full Name": fullName,
        Email: email,
        Phone: phone,
        Payment: paymentStatus,
        Timestamp: timestamp ? timestamp.toDate().toLocaleString() : "",
      })
    );

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "signups.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPDF = () => {
    if (signups.length === 0) return;
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Signups Report", 14, 22);

    const tableColumn = ["Full Name", "Email", "Phone", "Payment", "Timestamp"];
    const tableRows = [];

    signups.forEach(({ fullName, email, phone, paymentStatus, timestamp }) => {
      const row = [
        fullName,
        email,
        phone,
        paymentStatus,
        timestamp ? timestamp.toDate().toLocaleString() : "",
      ];
      tableRows.push(row);
    });

    autoTable(doc, {
      startY: 30,
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [33, 150, 243] },
    });

    doc.save("signups_report.pdf");
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto relative">
      {/* Success Modal */}
      {successMessage && (
        <div
          className="fixed top-5 right-5 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in-down flex items-center space-x-4 max-w-xs"
          role="alert"
        >
          <span className="flex-grow font-semibold">{successMessage}</span>
          <button
            onClick={() => setSuccessMessage("")}
            aria-label="Close notification"
            className="hover:text-green-300 focus:outline-none"
          >
            &#x2715;
          </button>
        </div>
      )}

      {/* Title and Actions */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
        <h1
          className="text-2xl sm:text-3xl font-bold"
          data-aos="fade-down"
          data-aos-delay="100"
        >
          Admin Dashboard
        </h1>
        <div
          className="flex flex-col sm:flex-row gap-2"
          data-aos="fade-down"
          data-aos-delay="200"
        >
          <button
            onClick={exportCSV}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition w-full sm:w-auto"
          >
            Export CSV
          </button>
          <button
            onClick={exportPDF}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition w-full sm:w-auto"
          >
            Export PDF
          </button>
          <button
            onClick={handleLogout}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition w-full sm:w-auto"
          >
            Log Out
          </button>
        </div>
      </div>

      {/* Signups Table */}
      <div
        className="overflow-x-auto rounded shadow border border-gray-200"
        data-aos="fade-up"
        data-aos-delay="300"
      >
        <table className="min-w-full text-sm md:text-base">
          <thead className="bg-gray-100 text-gray-700 text-left">
            <tr>
              <th className="p-3">Full Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Payment</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {signups.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  No signups yet.
                </td>
              </tr>
            ) : (
              signups.map((s, index) => (
                <tr
                  key={s.id}
                  className={`border-t ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                >
                  <td className="p-3">{s.fullName}</td>
                  <td className="p-3">{s.email}</td>
                  <td className="p-3">{s.phone}</td>
                  <td className="p-3">
                    {s.paymentStatus === "Paid" ? (
                      <span className="text-green-600 font-semibold">Paid</span>
                    ) : (
                      <span className="text-yellow-600">Not Paid</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    {s.paymentStatus !== "Paid" && (
                      <button
                        onClick={() => markAsPaid(s.id)}
                        className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition text-sm"
                      >
                        Mark Paid
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Custom Animation CSS */}
      <style>{`
        @keyframes fadeInDown {
          0% { opacity: 0; transform: translateY(-20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-down {
          animation: fadeInDown 0.4s ease forwards;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
